'use client';

import useValidator from '@/hooks/validator.hook';
import useTranslation from 'next-translate/useTranslation';
import { Button } from '../../button/Button';
import Link from 'next/link';
import { handleRecoverPassword } from './action';
import { FormEvent } from 'react';
import AuthBlock from '@/components/authBlock/AuthBlock.tsx';
import useToast from '@/hooks/toast.hook';
import { useRouter } from 'next/navigation';
import recoveryFormSchema from './schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { routeInfo } from '@/lib/_utils/routing';

function PasswordRecover() {
	const { t } = useTranslation('common');
	const { openToast } = useToast();
	const router = useRouter();

	const onSubmit = async ({ email }: { email: string }) => {
		await handleRecoverPassword(email);

		openToast({
			text: 'Success',
			fields: ['Check your email'],
			status: 'success',
			time: 3000,
		});

		router.push('/auth/signin');
	};

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(recoveryFormSchema) });

	useValidator(errors);

	const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		try {
			handleSubmit(onSubmit)(event);
		} catch (error) {
			openToast({
				text: t('validations:general'),
				fields: ['An error occurred'],
				status: 'danger',
				time: 3000,
			});
		}
	};

	return (
		<div className={`align-center flex h-full flex-col justify-center p-[30px] sm:p-[24px]`}>
			<AuthBlock title={t('reset_password')}>
				<form onSubmit={handleFormSubmit} className={`grid gap-[20px]`}>
					<div>
						<div className={`form-field floating-placeholder`}>
							<input
								type='email'
								placeholder={t('email')}
								{...register('email')}
								className={`${errors.email ? 'err' : ''}`}
							/>
							<div className={'placeholder'}>{t('email')}</div>
						</div>
					</div>
					<div className={`grid gap-[20px]`}>
						<Button variant='primary' fullwidth={true} type='submit'>
							{t('send_email')}
						</Button>
						<div>
							<span className={`mr-[6px] font-medium`}>{t('already_have_account')}</span>
							<Link href={routeInfo({ t, page: 'signin' }).path} className={`link`}>
								{routeInfo({ t, page: 'signin' }).label} ›
							</Link>
						</div>
					</div>
				</form>
			</AuthBlock>
		</div>
	);
}

export default PasswordRecover;
